import { useMutation, useQuery } from '@apollo/client';
import Address from '@website/components/forms/fields/Address';
import { SubmitButton } from '@website/components/forms/fields/SubmitButton';
import useGeolocation from '@website/hooks/geoLocation/useGeolocation';
import ErrorPage from '@website/components/utilities/ErrorPage';
import LoadingPage from '@website/components/utilities/LoadingPage';
import { Store } from '@website/Store';
import {
  CountryCodes,
  Currency,
  GetHostPaymentDetailsByUrlDocument,
  GetHostPaymentDetailsByUrlQuery,
  HostPaymentInfoInput,
  UpdateHostPaymentDetailsDocument,
} from '@website/types/graphqlOperations';
import Link from 'next/link';
import Router from 'next/router';
import React, { useEffect, useRef, useContext } from 'react';
import { Alert, Breadcrumb, Button, Form } from 'react-bootstrap';
import { useForm, UseFormWatch } from 'react-hook-form';

interface HostPaymentInfoFormProps {
  host: GetHostPaymentDetailsByUrlQuery['hostByURL'];
}

const currencyList: Currency[] = [
  Currency.GBP,
  Currency.USD,
  Currency.EUR,
  Currency.NOK,
];

/**
 * Copy host payment settings to the clipboard
 *
 * Admin only
 *
 * Used when messaging TMT with the updated details
 */
export function copyToClipboard(
  watch: UseFormWatch<HostPaymentInfoInput>,
  host: GetHostPaymentDetailsByUrlQuery['hostByURL'],
) {
  const settings = watch();

  const prefix = settings.tmtID
    ? 'Please can you update this affiliate?'
    : 'Please can you add the following affiliate?';

  const text = `
Hi,

${prefix}

## Affiliate Details

Name: ${host.name}
Legal Name: ${host.legalName}
TMT ID: ${settings.tmtID}
Currency: ${settings.currency}

## Bank Details

Name: ${settings.bankDetails.bankAccountName}
Bank Name: ${settings.bankDetails.bankName}
Account number: ${settings.bankDetails.accountNumber}
IBAN: ${settings.bankDetails.ibanNumber}
Sort: ${settings.bankDetails.sortCode}
Routing: ${settings.bankDetails.routingCode}
SWIFT: ${settings.bankDetails.swiftCode}

## Bank Address

${settings.bankDetails.bankAddress.addressLine1}
${settings.bankDetails.bankAddress.addressLine2 ?? ''}
${settings.bankDetails.bankAddress.dependentLocality ?? ''}
${settings.bankDetails.bankAddress.locality ?? ''}
${settings.bankDetails.bankAddress.administrativeArea ?? ''}
${settings.bankDetails.bankAddress.postalCode ?? ''}
${settings.bankDetails.bankAddress.country}`;

  window.navigator.clipboard.writeText(text);
}

export function HostPaymentInfoForm({ host }: HostPaymentInfoFormProps) {
  const errorMessageRef = useRef<HTMLInputElement>(null);
  const { state } = useContext(Store);
  const geoLocation = useGeolocation();

  const [updateHostDetails, { loading: isMutationLoading, error }] =
    useMutation(UpdateHostPaymentDetailsDocument);

  useEffect(() => {
    // scroll to the point where error message is
    if (error && errorMessageRef.current) {
      errorMessageRef.current.scrollIntoView();
    }
  }, [error]);

  const { paymentDetails } = host ?? {};

  const methods = useForm<HostPaymentInfoInput>({
    mode: 'onBlur',
    defaultValues: {
      hostID: host.id,
      ...paymentDetails,
      bankDetails: {
        ...paymentDetails?.bankDetails,
        bankAddress: {
          ...paymentDetails?.bankDetails?.bankAddress,
          country:
            paymentDetails?.bankDetails?.bankAddress?.country ||
            geoLocation?.country?.code ||
            CountryCodes.GB,
          ...paymentDetails?.bankDetails?.bankAddress,
        },
      },
    },
  });

  const {
    handleSubmit,
    register,
    formState: { errors },
    watch,
  } = methods;

  const isAdmin = state.user.roles.includes('admin');

  const submitHostProfileForm = async (formValues: HostPaymentInfoInput) => {
    const { tmtID, tmtIDLegacy, ...formValuesWithoutTMTDetails } = formValues;

    // Otherwise convert the tmtID to an integer, as HTML input fields return numbers as strings
    const parsedTMTID = parseInt(tmtID as unknown as string);
    const isCurrencySavedInDB = !!host.paymentDetails?.currency;
    const input = {
      ...formValuesWithoutTMTDetails,
      ...(isAdmin && { tmtID: parsedTMTID, tmtIDLegacy }),
      // override currency if it has been saved in the database
      ...(isCurrencySavedInDB && { currency: host.paymentDetails?.currency }),
    } as const;

    await updateHostDetails({
      variables: { input },
    });

    // redirect on success
    Router.push(`/hosts/dashboard/${host.urlSlug}`);
  };

  const isHostEditingDisabled = !isAdmin && !!paymentDetails?.tmtID;

  return (
    <>
      <Breadcrumb className="py-3">
        <Link href={`/hosts/dashboard/${host.urlSlug}`} passHref legacyBehavior>
          <Breadcrumb.Item href="">Host Dashboard</Breadcrumb.Item>
        </Link>
        <Breadcrumb.Item>Edit</Breadcrumb.Item>
        <Breadcrumb.Item active>Host Profile</Breadcrumb.Item>
      </Breadcrumb>
      <h1>{host.name}</h1>
      <h2>Update Payment Information</h2>
      {isHostEditingDisabled && (
        <Alert variant="warning" className="mt-4 mb-4">
          Your payment details have already been set and cannot be updated. If
          you need to adjust your payment details, please{' '}
          <Link href="/help/contact">contact us</Link>.
        </Alert>
      )}

      {error && (
        <Alert ref={errorMessageRef} variant="warning">
          <Alert.Heading> Something went wrong</Alert.Heading>
          {JSON.stringify(error)}
        </Alert>
      )}
      <Form role="form" onSubmit={handleSubmit(submitHostProfileForm)}>
        {isAdmin && (
          <>
            <div className="d-grid gap-2">
              <Button
                className="mb-3"
                variant="outline-primary"
                onClick={() => copyToClipboard(watch, host)}
              >
                Copy settings to clipboard
              </Button>
            </div>
            <h3>Admin Settings</h3>

            <Form.Group controlId="tmtID">
              <Form.Label>TMT ID *</Form.Label>
              <Form.Control
                type="number"
                required
                {...register('tmtID', { required: true })}
              />
            </Form.Group>
            <Form.Group controlId="tmtIDLegacy">
              <Form.Label>Legacy TMT ID</Form.Label>
              <Form.Control {...register('tmtIDLegacy')} />
            </Form.Group>
          </>
        )}

        <h3>Bank Details</h3>

        <Form.Group controlId="bankAccountName">
          <Form.Label>Account Holder Name *</Form.Label>
          <Form.Text className="text-muted">
            Name of the business or person that the bank account is registered
            to.
          </Form.Text>
          <Form.Control
            type="text"
            required
            {...register('bankDetails.bankAccountName', { required: true })}
            isInvalid={!!errors?.bankDetails?.bankAccountName}
            disabled={isHostEditingDisabled}
          />
        </Form.Group>
        <Form.Group controlId="bankName">
          <Form.Label>Bank Name *</Form.Label>
          <Form.Text className="text-muted">E.g. Bank of America.</Form.Text>
          <Form.Control
            type="text"
            required
            {...register('bankDetails.bankName', { required: true })}
            isInvalid={!!errors?.bankDetails?.bankName}
            disabled={isHostEditingDisabled}
          />
        </Form.Group>

        <Form.Group controlId="accountNumber">
          <Form.Label>Account Number *</Form.Label>
          <Form.Control
            type="text"
            required
            {...register('bankDetails.accountNumber', { required: true })}
            isInvalid={!!errors?.bankDetails?.accountNumber}
            disabled={isHostEditingDisabled}
          />
        </Form.Group>

        <Form.Group controlId="ibanNumber">
          <Form.Label>IBAN Number</Form.Label>
          <Form.Control
            type="text"
            {...register('bankDetails.ibanNumber')}
            isInvalid={!!errors?.bankDetails?.ibanNumber}
            disabled={isHostEditingDisabled}
          />
        </Form.Group>

        <Form.Group controlId="sortCode">
          <Form.Label>Sort Code</Form.Label>
          <Form.Control
            type="text"
            {...register('bankDetails.sortCode')}
            isInvalid={!!errors?.bankDetails?.sortCode}
            disabled={isHostEditingDisabled}
          />
        </Form.Group>

        <Form.Group controlId="routingCode">
          <Form.Label>Routing Code</Form.Label>
          <Form.Control
            type="text"
            {...register('bankDetails.routingCode')}
            isInvalid={!!errors?.bankDetails?.routingCode}
            disabled={isHostEditingDisabled}
          />
        </Form.Group>

        <Form.Group controlId="swiftCode">
          <Form.Label>Swift / BIC Code</Form.Label>
          <Form.Control
            name="bankDetails.swiftCode"
            type="text"
            {...register('bankDetails.swiftCode')}
            isInvalid={!!errors?.bankDetails?.swiftCode}
            disabled={isHostEditingDisabled}
          />
        </Form.Group>
        <Form.Group controlId="currency">
          <Form.Label>Preferred Currency *</Form.Label>
          <Form.Text className="text-muted d-block">
            Cannot be changed once it is set.
          </Form.Text>
          <Form.Select
            {...register('currency', { required: true })}
            // Currency cannot be changed once it is first set
            disabled={!!paymentDetails?.currency}
          >
            {currencyList.map((code) => {
              return (
                <option value={code} key={code}>
                  {code}
                </option>
              );
            })}
          </Form.Select>
        </Form.Group>

        <h3 className="mt-4">Bank Address</h3>

        <Address
          localityRequired
          watch={watch}
          register={register}
          name="bankDetails.bankAddress"
          showNameFields={false}
          isDisabled={isHostEditingDisabled}
        />

        <Form.Text className="text-muted">
          The fields marked with an <strong>asterisk *</strong> are required.
        </Form.Text>
        <SubmitButton
          text="Save"
          submittingLabel="Saving"
          isSubmitting={isMutationLoading}
          isDisabled={isHostEditingDisabled}
        />
      </Form>
    </>
  );
}

interface HostPaymentInfoFormWrapperProps {
  urlSlug: string;
}

export default function HostPaymentInfoFormWrapper({
  urlSlug,
}: HostPaymentInfoFormWrapperProps) {
  const { loading, error, data } = useQuery(
    GetHostPaymentDetailsByUrlDocument,
    {
      variables: {
        urlSlug,
      },
      fetchPolicy: 'cache-and-network',
    },
  );

  if (loading) return <LoadingPage />;
  if (error) return <ErrorPage />;

  return <HostPaymentInfoForm host={data?.hostByURL} />;
}
